import ROLE_ACCESS from "./access.json";

const toAccessRights = (roleAccess) => {
  const pageAccess = {};

  Object.entries(roleAccess).forEach(([role, pages]) => {
    Object.entries(pages).forEach(([page, actions]) => {
      if (!pageAccess[page]) {
        pageAccess[page] = {};
      }
      actions.forEach((action) => {
        if (!pageAccess[page][action]) {
          pageAccess[page][action] = [];
        }
        pageAccess[page][action].push(role);
      });
    });
  });

  return pageAccess;
};

// Convert ROLE_ACCESS to ACCESS_RIGHTS
/**
 * Access rights configuration for different resources and actions.
 * Defines which roles have access to specific actions on resources.
 */
export const ACCESS_RIGHTS = toAccessRights(ROLE_ACCESS);

/**
 * List of roles
 * @typedef {"Super Admin" | "Community Admin" | "Group Leader" | "POC Attendant" | "Collector" | "User"} Roles
 */

/**
 * List of resources
 * @typedef {"dashboard" | "users" | "communities" | "pocs" | "materials" | "requests" | "transactions" | "news" | "groups" | "members" | "collections"} Resources
 */

/**
 * Class to handle role-based access control.
 */
export class RoleAccess {
  /**
   * Creates an instance of RoleAccess.
   * @param {Roles[]} roles - An array of roles assigned to the user.
   */
  constructor(roles) {
    this.roles = roles; // Accepts an array of roles
  }

  /**
   * Checks if the user has permission to perform a specific action on a resource.
   * @param {"view" | "add" | "update" | "deactivate"} action - The action to check (e.g., "view", "add", "update", "deactivate").
   * @param {Resources} resource - The resource to check access for (e.g., "dashboard", "users").
   * @returns {boolean} - Returns `true` if the user has access, otherwise `false`.
   */
  can(action, resource) {
    if (!ACCESS_RIGHTS[resource] || !ACCESS_RIGHTS[resource][action]) {
      return false;
    }

    return this.roles.some((role) =>
      ACCESS_RIGHTS[resource][action].includes(role)
    );
  }
}

// ==============================
// Test based on these scenarios:
// ==============================
// const user = new RoleAccess(["User", "Group Leader"]); // User has Member account role, and Group Leader for profile membership role

// console.log(user.can("view", "dashboard")); // true
// console.log(user.can("view", "users")); // false
// console.log(user.can("add", "users")); // false (only Community Admin or Super Admin can add Users)
// console.log(user.can("view", "news")); // true (Group Leader has access)
// console.log(user.can("add", "collections")); // false (Only POC Attendant can create Collection)
